/**
 * JSON-LD builders. Shared by the React runtime and by
 * scripts/generate-seo.js so the structured data in the static HTML and the
 * structured data after hydration are identical.
 */
const site = require('../content/site');

const ORG_ID = `${site.url}/#organization`;
const SITE_ID = `${site.url}/#website`;
const abs = (path) => `${site.url}${path === '/' ? '/' : path}`;

function organization() {
  return {
    '@type': ['Organization', 'ProfessionalService'],
    '@id': ORG_ID,
    name: site.name,
    alternateName: site.shortName,
    url: `${site.url}/`,
    logo: { '@type': 'ImageObject', url: `${site.url}/og-image.jpg` },
    image: `${site.url}/og-image.jpg`,
    description: site.description,
    email: site.email,
    foundingDate: site.founded,
    address: {
      '@type': 'PostalAddress',
      streetAddress: site.address.street,
      addressLocality: site.address.locality,
      addressRegion: site.address.region,
      addressCountry: site.address.country,
    },
    // Calgary is the registered address; delivery is worldwide. Listing both
    // keeps the local-pack signal without capping us to one market.
    areaServed: [
      { '@type': 'City', name: 'Calgary' },
      { '@type': 'AdministrativeArea', name: 'Alberta' },
      { '@type': 'Country', name: 'Canada' },
      { '@type': 'Place', name: 'Worldwide' },
    ],
    sameAs: [site.social.facebook, site.social.linkedin, site.social.instagram],
    geo: { '@type': 'GeoCoordinates', latitude: 50.9236, longitude: -114.0731 },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: site.email,
      areaServed: 'CA',
      availableLanguage: ['English'],
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
    knowsAbout: [
      'Web development', 'Mobile app development', 'Cloud architecture',
      'Cybersecurity', 'UI/UX design', 'Custom software development',
    ],
  };
}

function website() {
  return {
    '@type': 'WebSite',
    '@id': SITE_ID,
    url: `${site.url}/`,
    name: site.name,
    inLanguage: 'en-CA',
    publisher: { '@id': ORG_ID },
  };
}

function webPage({ path, title, description, image }) {
  return {
    '@type': 'WebPage',
    '@id': `${abs(path)}#webpage`,
    url: abs(path),
    name: title,
    description,
    primaryImageOfPage: { '@type': 'ImageObject', url: `${site.url}${image || '/og-image.jpg'}` },
    isPartOf: { '@id': SITE_ID },
    about: { '@id': ORG_ID },
    inLanguage: 'en-CA',
  };
}

/** Breadcrumbs give Google the path chip under the result instead of a raw URL. */
function breadcrumbs(trail) {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${abs(trail[trail.length - 1].path)}#breadcrumbs`,
    itemListElement: trail.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: abs(c.path),
    })),
  };
}

/** FAQPage is what earns the expandable Q&A block in search results. */
function faqPage(path, faqs) {
  return {
    '@type': 'FAQPage',
    '@id': `${abs(path)}#faq`,
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

function service(svc) {
  return {
    '@type': 'Service',
    '@id': `${abs(`/services/${svc.slug}`)}#service`,
    name: svc.name,
    description: svc.metaDescription,
    serviceType: svc.name,
    provider: { '@id': ORG_ID },
    areaServed: [
      { '@type': 'City', name: 'Calgary' },
      { '@type': 'Country', name: 'Canada' },
      { '@type': 'Place', name: 'Worldwide' },
    ],
    url: abs(`/services/${svc.slug}`),
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${svc.name} deliverables`,
      itemListElement: svc.features.map((f) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: f.title, description: f.desc },
      })),
    },
  };
}


/** Article schema — what gets a publish date and byline shown in results. */
function article(a) {
  const url = abs(`/insights/${a.slug}`);
  return {
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    headline: a.title,
    description: a.metaDescription,
    url,
    datePublished: a.published,
    dateModified: a.updated || a.published,
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${url}#webpage` },
    image: `${site.url}/og/insights-${a.slug}.jpg`,
    keywords: a.tags.join(', '),
    articleSection: a.category,
    inLanguage: 'en-CA',
    wordCount: a.body.reduce(
      (n, b) => n + ((b.p || b.h2 || b.h3 || b.quote || '') + (b.ul || []).join(' ')).split(/\s+/).filter(Boolean).length,
      0
    ),
  };
}

/** Industry pages describe the same services scoped to one audience. */
function industry(i) {
  const url = abs(`/industries/${i.slug}`);
  return {
    '@type': 'Service',
    '@id': `${url}#service`,
    name: `Technology services for ${i.name}`,
    description: i.metaDescription,
    serviceType: i.name,
    provider: { '@id': ORG_ID },
    audience: { '@type': 'BusinessAudience', name: i.name },
    areaServed: [
      { '@type': 'City', name: 'Calgary' },
      { '@type': 'Place', name: 'Worldwide' },
    ],
    url,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${i.name} solutions`,
      itemListElement: i.solutions.map((x) => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name: x.title, description: x.desc },
      })),
    },
  };
}

/**
 * A delivered project. CreativeWork with `creator` pointing at us is the
 * honest shape: we made the thing, the client owns and operates it.
 */
function caseStudy(w) {
  const url = abs(`/work/${w.slug}`);
  return {
    '@type': 'CreativeWork',
    '@id': `${url}#work`,
    name: `${w.name} — ${w.tagline}`,
    headline: w.name,
    description: w.metaDescription,
    url,
    creator: { '@id': ORG_ID },
    dateCreated: w.year,
    about: { '@type': 'WebSite', name: w.name, url: w.url },
    keywords: [w.sector, ...w.stack].join(', '),
    inLanguage: 'en-CA',
  };
}

/** Simple ordered list of links, used by the index pages. */
function itemList(path, items) {
  return {
    '@type': 'ItemList',
    '@id': `${abs(path)}#list`,
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      url: abs(it.path),
    })),
  };
}

/** Assembles the full @graph for one page. */
function graphFor({ path, title, description, image, extra = [] }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [organization(), website(), webPage({ path, title, description, image }), ...extra],
  };
}

module.exports = {
  abs, organization, website, webPage, breadcrumbs, faqPage, service,
  article, industry, caseStudy, itemList, graphFor, ORG_ID, SITE_ID,
};
